import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Autocomplete,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Card,
  CardContent,
  IconButton,
  Divider,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
  RadioGroup,
  FormControlLabel,
  Radio
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CompareArrows as TransferIcon,
  QrCode as QrCodeIcon,
  Search as SearchIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import { useInventory } from '../contexts/InventoryContext';
import { useJob } from '../contexts/JobContext';
import { inventoryApi, jobApi } from '../services/api.service';
import equipmentService from '../services/equipment.service';
import BarcodeScanner from './BarcodeScanner';
import { printQRCodes, printSingleQRCode } from '../services/qrCodePrintService';

/**
 * TransferInventory Component
 * Allows transferring items between jobs or to sites
 */
const TransferInventory = ({ 
  selectedEquipment = [], 
  hideBarcode = false, 
  mode = 'default',
  open = true,
  onClose,
  onTransferComplete 
}) => {
  // State for source and destination
  const [sourceJob, setSourceJob] = useState(null);
  const [destinationJob, setDestinationJob] = useState(null);
  const [destinationSite, setDestinationSite] = useState(null);
  const [availableJobs, setAvailableJobs] = useState([]);
  const [availableSites, setAvailableSites] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  
  // State for items to transfer
  const [transferItems, setTransferItems] = useState([]);
  const [itemToAdd, setItemToAdd] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [transferReason, setTransferReason] = useState('');
  
  // State for barcode scanning
  const [showScanner, setShowScanner] = useState(false);
  const [scanTarget, setScanTarget] = useState('search'); // 'search' or 'add'

  // State for UI feedback
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [sourceJobItems, setSourceJobItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  
  // Get context data
  const { warehouses } = useInventory();
  const { currentJob } = useJob();
  
  // Load available jobs on component mount
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setJobsLoading(true);
        
        // Load jobs from API
        try {
          const response = await jobApi.getAllJobs();
          console.log('Job API response:', response);
          if (response && response.data) {
            // Handle both array and object with jobs property
            const jobs = Array.isArray(response.data) ? response.data : response.data.jobs || [];
            console.log('Setting available jobs from API:', jobs);
            setAvailableJobs(jobs);
          } else {
            console.log('No jobs found in API response');
            setAvailableJobs([]);
          }
        } catch (apiError) {
          console.log('Error with job API:', apiError);
          setError('Failed to load jobs');
          setAvailableJobs([]);
        }
      } catch (err) {
        console.error('Error in job loading process:', err);
        setError('Failed to load available jobs.');
        setAvailableJobs([]);
      } finally {
        setJobsLoading(false);
      }
    };
    
    fetchJobs();
  }, []);

  // Load sites for selected destination job
  useEffect(() => {
    if (destinationJob && destinationJob.sites && Array.isArray(destinationJob.sites)) {
      setAvailableSites(destinationJob.sites);
    } else {
      setAvailableSites([]);
      setDestinationSite(null);
    }
  }, [destinationJob]);

  // Handle pre-selected equipment (when called from StatusHistoryDialog)
  useEffect(() => {
    if (selectedEquipment.length > 0 && mode === 'specific-equipment') {
      const equipment = selectedEquipment[0];
      
      // Pre-populate transfer items with the selected equipment
      const transferItem = {
        id: equipment.equipment_id || equipment.barcode || equipment.id,
        description: equipment.description || `Equipment ${equipment.id}`,
        manufacturer: equipment.manufacturer || '',
        model: equipment.model || '',
        serial_number: equipment.serial_number || '',
        quantity: 1,
        currentJob: equipment.current_job_name || equipment.current_job_id || '',
        warehouse: equipment.warehouse_name || equipment.warehouse || '',
        rack: equipment.rack_name || equipment.rack || equipment.zone_name || equipment.zone || '',
        bay: equipment.bay_name || equipment.bay || ''
      };
      
      setTransferItems([transferItem]);
      
      // Set source job if equipment is currently assigned
      if (equipment.current_job_id) {
        const sourceJobData = {
          id: equipment.current_job_id,
          name: equipment.current_job_name || `Job ${equipment.current_job_id}`
        };
        setSourceJob(sourceJobData);
      }
    }
  }, [selectedEquipment, mode]);
  
  // If there's a current job in context, use it as destination by default
  useEffect(() => {
    if (currentJob) {
      setDestinationJob(currentJob);
    }
  }, [currentJob]);
  
  // Function to fetch items from the source job
  const fetchItems = useCallback(async () => {
    if (!sourceJob) {
      setSourceJobItems([]);
      return;
    }
    
    try {
      setItemsLoading(true);
      
      try {
        // Try getting from API first
        const response = await jobApi.getJobEquipment(sourceJob.id);
        if (response && response.data) {
          // Ensure response.data is an array
          const items = Array.isArray(response.data) ? response.data : [];
          setSourceJobItems(items);
        } else {
          console.log('No data from API');
          setSourceJobItems([]);
        }
      } catch (apiErr) {
        console.log('Error with API:', apiErr);
        setSourceJobItems([]);
      }
    } catch (err) {
      console.error('Failed to fetch equipment from source job:', err);
      setError('Failed to load equipment from source job.');
      setSourceJobItems([]);
    } finally {
      setItemsLoading(false);
    }
  }, [sourceJob]);
  
  // Fetch items when source job changes
  useEffect(() => {
    if (sourceJob) {
      fetchItems();
    }
  }, [sourceJob, fetchItems]);
  
  // Handle barcode scan
  const handleScanResult = (result) => {
    let itemId = result;
    
    // Try to parse QR code JSON data
    try {
      // Check if it's URL encoded JSON (from QR codes)
      const decoded = decodeURIComponent(result);
      const qrData = JSON.parse(decoded);
      
      // Extract the equipment ID from QR code data
      if (qrData.id) {
        itemId = qrData.id;
        console.log('Parsed QR code data:', qrData);
      }
    } catch (e) {
      // Not JSON, use the raw barcode value
      console.log('Using raw barcode value:', result);
    }
    
    if (scanTarget === 'search') {
      setItemToAdd(itemId);
    }
    setShowScanner(false);
  };
  
  // Add item to transfer list
  const handleAddItem = () => {
    if (!itemToAdd) {
      setError('Please enter an item ID.');
      return;
    }
    
    if (!sourceJob) {
      setError('Please select a source job first.');
      return;
    }
    
    // Find the item in the source job equipment
    const item = sourceJobItems.find(i => i.id === itemToAdd || i.barcode === itemToAdd);
    
    if (!item) {
      setError(`Item ${itemToAdd} not found in source job equipment.`);
      return;
    }
    
    // Find matching equipment data for this item
    const sourceItem = sourceJobItems.find(i => i.id === item.id);
    
    if (!sourceItem) {
      setError(`Item ${item.id} not found in source job equipment.`);
      return;
    }
    
    // Use current quantity input value, bounded by available quantity
    const requestedQuantity = Math.min(
      parseInt(quantity, 10) || 1,
      sourceItem.quantity
    );
    
    // Validate that requested quantity is valid
    if (requestedQuantity <= 0) {
      setError('Quantity must be greater than zero.');
      return;
    }
    
    // Check if item is already in the transfer list
    const existingItemIndex = transferItems.findIndex(i => i.id === item.id);
    
    // If the item already exists, don't add it again
    if (existingItemIndex >= 0) {
      return;
    }
    
    // For new items, the quantity is what the user selected
    const newQuantity = requestedQuantity;
    
    // Check if the quantity exceeds what's available in the source job
    if (newQuantity > sourceItem.quantity) {
      setError(`Insufficient quantity available for ${item.id}. Available: ${sourceItem.quantity}, Requested: ${newQuantity}`);
      return;
    }
    
    // We've already handled the case where the item exists earlier and returned
    // So here we just need to add the new item
    setTransferItems([
      ...transferItems,
      {
        ...item,
        quantity: newQuantity
      }
    ]);
    
    setItemToAdd('');
    setQuantity(1);
    setError('');
  };
  
  // Remove item from transfer list
  const handleRemoveItem = (itemId) => {
    setTransferItems(transferItems.filter(item => item.id !== itemId));
  };
  
  // Submit transfer
  const handleSubmit = async () => {
    // For specific-equipment mode
    if (mode === 'specific-equipment') {
      if (transferItems.length === 0) {
        setError('No equipment selected for transfer.');
        return;
      }
      
      if (!destinationJob) {
        setError('Please select a destination job.');
        return;
      }
      
      if (!transferReason) {
        setError('Please select a reason for transfer.');
        return;
      }
      
      try {
        setLoading(true);
        setError('');
        setSuccess('');
        
        const item = transferItems[0];
        const transferData = {
          from_job_id: sourceJob?.id || null,
          to_job_id: destinationJob?.id || null,
          to_site_id: destinationSite?.id || null,
          notes: `Transfer reason: ${transferReason}`,
          transferred_by: 'current_user' // TODO: Get from auth context
        };
        
        // V2: Transfer the equipment using equipment service
        await equipmentService.transferEquipment(item.id, transferData);
        
        setSuccess('Equipment transferred successfully!');
        
        // Call completion callback if provided
        if (onTransferComplete) {
          onTransferComplete();
        }
        
        // Close dialog if provided
        if (onClose) {
          onClose();
        }
      } catch (err) {
        console.error('Transfer error:', err);
        setError(err.response?.data?.message || 'Failed to transfer equipment. Please try again.');
      } finally {
        setLoading(false);
      }
      
      return;
    }
    
    // Normal mode - multiple items
    if (transferItems.length === 0) {
      setError('Please add at least one item to transfer.');
      return;
    }
    
    if (!destinationJob) {
      setError('Please select a destination job.');
      return;
    }
    
    if (!transferReason) {
      setError('Please select a reason for transfer.');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      // V2: Transfer each equipment item individually
      const transferPromises = transferItems.map(async (item) => {
        const transferData = {
          from_job_id: sourceJob?.id || null,
          to_job_id: destinationJob.id || null,
          to_site_id: destinationSite?.id || null,
          notes: `Transfer reason: ${transferReason}`,
          transferred_by: 'current_user' // TODO: Get from auth context
        };
        
        try {
          await equipmentService.transferEquipment(item.id, transferData);
          return { success: true, item_id: item.id };
        } catch (error) {
          console.error(`Failed to transfer equipment ${item.id}:`, error);
          return { success: false, item_id: item.id, error };
        }
      });
      
      const results = await Promise.allSettled(transferPromises);
      const successes = results.filter(r => r.status === 'fulfilled' && r.value.success);
      const failures = results.filter(r => r.status === 'rejected' || !r.value.success);
      
      if (failures.length > 0) {
        throw new Error(`${failures.length}/${transferItems.length} transfers failed`);
      }
      
      setSuccess('Items transferred successfully!');
      setTransferItems([]);
      setTransferReason('');
      
      // Refresh the source job items
      fetchItems();
    } catch (err) {
      console.error('Transfer error:', err);
      setError(err.response?.data?.message || 'Failed to transfer items. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Calculate total items for pagination
  const totalItems = sourceJobItems.length;
  
  return (
    <Box sx={{ p: 2 }}>
      {/* Alert messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      
      {/* Transfer Details Section */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" component="h3" gutterBottom sx={{ mb: 2 }}>
              {mode === 'specific-equipment' ? 'Transfer' : 'Transfer Details'}
            </Typography>

            <Grid container spacing={2}>
              
              {/* Source Job Selector (for normal mode) */}
              {mode !== 'specific-equipment' && (
                <Grid item xs={12}>
                  <FormControl fullWidth margin="normal" sx={{ minWidth: 200 }}>
                    <InputLabel id="source-job-label">Source Job</InputLabel>
                    <Select
                      labelId="source-job-label"
                      value={sourceJob ? sourceJob.id : ''}
                      onChange={(e) => {
                        const newJob = availableJobs.find(job => job.id === e.target.value);
                        setSourceJob(newJob);
                        setTransferItems([]); // Clear transfer list when source changes
                      }}
                      label="Source Job"
                      disabled={loading || jobsLoading}
                      sx={{ minWidth: 200 }}
                    >
                      <MenuItem value="">
                        <em>Select a source job</em>
                      </MenuItem>
                      {availableJobs.map(job => (
                        <MenuItem key={job.id} value={job.id}>
                          {job.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {/* Simplified inline layout for specific-equipment mode */}
              {mode === 'specific-equipment' && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                    {/* Destination Job Selector */}
                    <FormControl sx={{ flex: 1, minWidth: 180, maxWidth: 250 }}>
                      <InputLabel id="destination-job-label">Destination Job</InputLabel>
                      <Select
                        labelId="destination-job-label"
                        value={destinationJob ? destinationJob.id : ''}
                        onChange={(e) => {
                          const newJob = availableJobs.find(job => job.id === e.target.value);
                          setDestinationJob(newJob);
                        }}
                        label="Destination Job"
                        disabled={loading || jobsLoading}
                      >
                        <MenuItem value="">
                          <em>Select destination job</em>
                        </MenuItem>
                        {availableJobs
                          .filter(job => !sourceJob || job.id !== sourceJob.id)
                          .map(job => (
                            <MenuItem key={job.id} value={job.id}>
                              {job.name}
                            </MenuItem>
                          ))
                        }
                      </Select>
                    </FormControl>

                    {/* Site Selector - shown after job selected */}
                    {destinationJob && availableSites.length > 0 && (
                      <FormControl sx={{ flex: 1, minWidth: 180, maxWidth: 250 }}>
                        <InputLabel id="destination-site-label">Site (Optional)</InputLabel>
                        <Select
                          labelId="destination-site-label"
                          value={destinationSite ? destinationSite.id : ''}
                          onChange={(e) => {
                            const newSite = availableSites.find(site => site.id === e.target.value);
                            setDestinationSite(newSite);
                          }}
                          label="Site (Optional)"
                          disabled={loading}
                        >
                          <MenuItem value="">
                            <em>None</em>
                          </MenuItem>
                          {availableSites.map(site => (
                            <MenuItem key={site.id} value={site.id}>
                              {site.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}

                    {/* Transfer Reason */}
                    <FormControl sx={{ flex: 1, minWidth: 180, maxWidth: 250 }}>
                      <InputLabel id="transfer-reason-label">Reason for Transfer</InputLabel>
                      <Select
                        labelId="transfer-reason-label"
                        value={transferReason}
                        onChange={(e) => setTransferReason(e.target.value)}
                        label="Reason for Transfer"
                        disabled={loading}
                      >
                        <MenuItem value="">
                          <em>Select a reason</em>
                        </MenuItem>
                        <MenuItem value="Equipment needed urgently">Equipment needed urgently</MenuItem>
                        <MenuItem value="Job priority changed">Job priority changed</MenuItem>
                        <MenuItem value="Equipment no longer needed">Equipment no longer needed</MenuItem>
                        <MenuItem value="Incorrect assignment">Incorrect assignment</MenuItem>
                        <MenuItem value="Other">Other</MenuItem>
                      </Select>
                    </FormControl>

                    {/* Transfer Button - vertically aligned */}
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSubmit}
                      disabled={loading || !destinationJob || !transferReason}
                      sx={{ minWidth: 150, height: 56 }}
                    >
                      {loading ? 'Processing...' : 'Transfer'}
                    </Button>
                  </Box>
                </Grid>
              )}

              {/* Original layout for normal mode */}
              {mode !== 'specific-equipment' && (
                <>
                  {/* Destination Job Selector */}
                  <Grid item xs={12}>
                    <FormControl fullWidth margin="normal" sx={{ minWidth: 200 }}>
                      <InputLabel id="destination-job-label">Destination Job</InputLabel>
                      <Select
                        labelId="destination-job-label"
                        value={destinationJob ? destinationJob.id : ''}
                        onChange={(e) => {
                          const newJob = availableJobs.find(job => job.id === e.target.value);
                          setDestinationJob(newJob);
                        }}
                        label="Destination Job"
                        disabled={loading || jobsLoading}
                        sx={{ minWidth: 200 }}
                      >
                        <MenuItem value="">
                          <em>Select a destination job</em>
                        </MenuItem>
                        {availableJobs
                          .filter(job => !sourceJob || job.id !== sourceJob.id)
                          .map(job => (
                            <MenuItem key={job.id} value={job.id}>
                              <Box>
                                <Typography variant="body1">{job.name}</Typography>
                                <Typography variant="caption" color="text.secondary">ID: {job.id}</Typography>
                              </Box>
                            </MenuItem>
                          ))
                        }
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Transfer Reason */}
                  <Grid item xs={12}>
                    <FormControl fullWidth margin="normal" sx={{ minWidth: 200 }}>
                      <InputLabel id="transfer-reason-label">Reason for Transfer</InputLabel>
                      <Select
                        labelId="transfer-reason-label"
                        value={transferReason}
                        onChange={(e) => setTransferReason(e.target.value)}
                        label="Reason for Transfer"
                        disabled={loading}
                        sx={{ minWidth: 200 }}
                      >
                        <MenuItem value="">
                          <em>Select a reason</em>
                        </MenuItem>
                        <MenuItem value="Equipment needed urgently">Equipment needed urgently</MenuItem>
                        <MenuItem value="Job priority changed">Job priority changed</MenuItem>
                        <MenuItem value="Equipment no longer needed">Equipment no longer needed</MenuItem>
                        <MenuItem value="Incorrect assignment">Incorrect assignment</MenuItem>
                        <MenuItem value="Other">Other</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Submit Button */}
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSubmit}
                      startIcon={<TransferIcon />}
                      fullWidth
                      disabled={loading || transferItems.length === 0 || !transferReason || !destinationJob}
                    >
                      {loading ? 'Processing...' : 'Transfer All Items'}
                    </Button>
                  </Grid>
                </>
              )}
            </Grid>
          </Paper>
        </Grid>
        
        {/* Items to Transfer Section (Hidden for specific-equipment mode) */}
        {mode !== 'specific-equipment' && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" component="h3" gutterBottom sx={{ mb: 2 }}>
                Items to Transfer
              </Typography>
              
              {transferItems.length === 0 ? (
                <Box py={3} textAlign="center">
                  <Typography variant="body1" color="textSecondary">
                    No items added to transfer list yet.
                  </Typography>
                </Box>
              ) : (
                <Box>
                  {transferItems.map((item, index) => (
                    <Card key={index} variant="outlined" sx={{ mb: 1 }}>
                      <CardContent sx={{ pb: 1 }}>
                        <Grid container alignItems="center">
                          <Grid item xs>
                            <Typography variant="subtitle1" component="div">
                              {item.description}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              ID: {item.id}
                            </Typography>
                            {item.manufacturer && (
                              <Typography variant="body2" color="textSecondary">
                                {item.manufacturer} {item.model || ''}
                              </Typography>
                            )}
                            <Chip
                              size="small"
                              label={`Qty: ${item.quantity}`}
                              color="primary"
                              variant="outlined"
                              sx={{ mt: 0.5 }}
                            />
                          </Grid>
                          <Grid item>
                            <IconButton
                              onClick={() => handleRemoveItem(item.id)}
                              size="small"
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  ))}
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Typography variant="body2" color="textSecondary" textAlign="right">
                    Total items to transfer: {transferItems.reduce((sum, item) => sum + item.quantity, 0)}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        )}
      </Grid>
      
      {/* Source Job Equipment Section (Hidden for specific-equipment mode) */}
      {mode !== 'specific-equipment' && sourceJob && (
        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Available Equipment in {sourceJob.name}
          </Typography>
          
          {itemsLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : sourceJobItems.length === 0 ? (
            <Box py={3} textAlign="center">
              <Typography variant="body1" color="textSecondary">
                No equipment found in this job.
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Equipment ID"
                      value={itemToAdd}
                      onChange={(e) => setItemToAdd(e.target.value)}
                      placeholder="Enter or scan equipment ID"
                      InputProps={{
                        endAdornment: (
                          <IconButton
                            onClick={() => {
                              setScanTarget('search');
                              setShowScanner(true);
                            }}
                            size="small"
                          >
                            <QrCodeIcon />
                          </IconButton>
                        )
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label="Quantity"
                      type="number"
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                      inputProps={{ min: 1 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleAddItem}
                      startIcon={<AddIcon />}
                      fullWidth
                    >
                      Add to Transfer
                    </Button>
                  </Grid>
                </Grid>
              </Box>
              
              <TableContainer>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Manufacturer</TableCell>
                      <TableCell>Model</TableCell>
                      <TableCell align="center">Available</TableCell>
                      <TableCell align="center">In Transfer</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sourceJobItems
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((item, index) => {
                        const inTransfer = transferItems.find(ti => ti.id === item.id);
                        const inTransferQty = inTransfer ? inTransfer.quantity : 0;
                        
                        return (
                          <TableRow key={index} hover>
                            <TableCell>{item.id}</TableCell>
                            <TableCell>{item.description}</TableCell>
                            <TableCell>{item.manufacturer || '—'}</TableCell>
                            <TableCell>{item.model || '—'}</TableCell>
                            <TableCell align="center">{item.quantity - inTransferQty}</TableCell>
                            <TableCell align="center">
                              {inTransferQty > 0 && (
                                <Chip
                                  size="small"
                                  label={inTransferQty}
                                  color="primary"
                                />
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <TablePagination
                component="div"
                count={totalItems}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
              />
            </>
          )}
        </Paper>
      )}
      
      {/* Barcode Scanner Dialog */}
      <Dialog
        open={showScanner}
        onClose={() => setShowScanner(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Scan Equipment Barcode
          <IconButton
            onClick={() => setShowScanner(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <BarcodeScanner
            onScanSuccess={handleScanResult}
            onScanError={(err) => {
              setError(err.message);
              setShowScanner(false);
            }}
          />
        </DialogContent>
      </Dialog>
      
    </Box>
  );
};

export default TransferInventory;